import { createContext, ReactNode, SetStateAction, useMemo, useState } from 'react';
import QRCode from 'react-qr-code';
import {
  CheckCircleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  InfoCircleOutlined,
} from '@ant-design/icons';
import useCountDown from '@livelybone/use-count-down';
import { useRequest } from 'ahooks';
import { Button, Form, Input, List, message, Modal } from 'antd';

import { getErrorMsg } from '@/utils/fns';

import { generateMfaSecret, sendCode, verifyCode, saveMfaSecret, unbindMfa } from '@/api/services/userService';
import { logout } from '@/api/apiClient';
import { useUserInfo } from '@/store/userStore';

interface MyContextState {
  captcha?: string;
}

export const MyContext = createContext<{
  data: MyContextState;
  setData(d: SetStateAction<MyContextState>): void;
}>({
  data: {},
  setData: () => {},
});

const ModalContainer = ({
  children,
  open,
  title,
  onCancel,
  bodyPadding,
  footer,
  ...config
}: {
  children: ReactNode;
  open: boolean;
  title: ReactNode | string;
  onCancel: () => void;
  bodyPadding?: string | number;
  footer?: ReactNode | boolean;
  [key: string]: unknown;
}) => {
  return (
    <>
      <Modal
        open={open}
        maskClosable={false}
        bodyStyle={{ padding: 0 }}
        width={420}
        onCancel={onCancel}
        destroyOnClose
        title={title}
        {...config}
      >
        {children}
      </Modal>
    </>
  );
};

export const CheckEmail = ({
  open,
  setOpen,
  onSuccess,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  onSuccess: (captcha: string) => void;
}) => {
  const [isReset, setIsReset] = useState(false);
  const countDown = useCountDown(60);
  const [form] = Form.useForm<{email: string, captcha: string}>();

  const { email, bindMfaStatus } = useUserInfo();

  const [captcha, setCode] = useState<string>('');

  const { runAsync: check, loading: checkLoading } = useRequest(
    (params: { email: string; captcha: string }) =>
      verifyCode(params),
    {
      manual: true,
      onSuccess: () => onSuccess(captcha),
    },
  );
  const {runAsync: runCode, loading: codeLoading} = useRequest(
    () => {
      if (!email) throw new Error('Email is invalid!');
      return sendCode({ email });
    },
    { manual: true },
  );

  return (
    <ModalContainer
      open={open}
      title={
        bindMfaStatus
          ? 'Disable Google 2FA'
          : 'Set up Google Two-Factor Authentication'
      }
      onCancel={() => setOpen(false)}
      onOk={async () => {
        await form.validateFields();
        if (captcha.length === 6) check({ email: email || '', captcha });
      }}
      confirmLoading={checkLoading}
      okText="Confirm"
      afterCose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical">
        <div className='mb-8'>
          Please input the verification code sent to
        </div>
        <div className='text-xl font-bold mb-4 text-center'>{email}</div>
        <div className="flex flex-col">
          {email ? (
            <div
              className='font-normal text-sm leading-[18px] text-right text-primary'
            >
              <Button
                type='text'
                className='text-primary!'
                onClick={() => {
                  if (countDown.isRunning) return;
                  setIsReset(true);
                  runCode().then(() => countDown.start())
                    .then(() => message.success('Code has been sent'))
                    .catch((err: any) => message.error(getErrorMsg(err)));
                }}
                disabled={countDown.isRunning}
                loading={codeLoading}
              >
                {isReset ? 'Resend code' : 'Send code'}
                {countDown.isRunning ? ` (${countDown.count}s)` : ''}
              </Button>
            </div>
          ) : (
            <div className='text-right font-sm color-[#faad14]'>
              <InfoCircleOutlined /> Please contact BD to set up contact email
              first.
            </div>
          )}
          <Form.Item
            name="captcha"
            rules={[{ required: true }]}
          >
            <div className='text-center'>
              <Input.OTP
                size='large'
                style={{ gap: 10 }}
                separator={() => <span>-</span>}
                length={6}
                onChange={(value) => setCode(value)}
              />
            </div>
          </Form.Item>
        </div>
      </Form>
    </ModalContainer>
  );
};
const data = [
  {
    title: 'Download Google Authenticator',
    detail: 'Google Authenticator can be downloaded from the App store or Google Play.Search “Google Authenticator” and proceed to download.'
  },
  {
    title: 'Add key phrase into Google Authenticator and remember the key',
    detail: 'Open Google Authenticator, scan the QR code below or manually enter the key phrase to activate the verification token. Key phrase is used to recover Google Authenticator in the event of a loss or change of device — please make sure to keep the key phrase safe before setting up Google Authenticator'
  }
]
export const SetUpTFA = ({
  open,
  setOpen,
  captcha,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  captcha: string;
}) => {
  const [form] = Form.useForm();
  const { email = '' } = useUserInfo();
  const { data: mfaData, loading: generateLoading } = useRequest(
    () => {
      if (!captcha) throw new Error('Captcha is invalid!');
      return generateMfaSecret({ email, captcha });
    },
    {
      ready: !!captcha,
    },
  );
  
  const mfaSecret = useMemo(() => mfaData?.mfaSecret, [mfaData]);
  const { runAsync: handleSaveMfaSecret, loading: saveMfaLoading } = useRequest(
    (mfaCode: string) => {
      return saveMfaSecret({ mfaCode });
    },
    {
      manual: true,
      ready: !!mfaSecret,
    },
  );
  return (
    <ModalContainer
      open={open}
      title={'Set up Google Two-Factor Authentication'}
      onCancel={() => setOpen(false)}
      onOk={() => {
        form
          .validateFields()
          .then(() => {
            const mfaCode = form.getFieldValue('mfaCode') ?? '';
            if ((mfaCode ?? '').length < 6) {
              return Promise.reject(
                'Please enter the Google Authenticator code',
              );
            }
            handleSaveMfaSecret(mfaCode)
              .then(() => {
                message.success('Completed 2FA set up');
                logout();
              })
              .catch(err => message.error(err.message));
          })
          .catch(err => message.error('Invalid 2FA Code'));
      }}
      loading={generateLoading}
      confirmLoading={saveMfaLoading}
      afterClose={() => form.resetFields()}
      okText="Confirm"
    >
      <Form form={form} layout="vertical">
        <List
          pagination={false}
          dataSource={data}
          split={false}
          renderItem={(item: { title: string, detail: string }, index) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <div className='flex gap-2'>
                    <span>{index + 1}.</span>
                    <span className='text-shadow-xs'>{item.title}</span>
                  </div>
                }
                description={item.detail}
              />
            </List.Item>
          )}
        />
        <div className="flex flex-wrap justify-around mt-2 gap-2 mb-2">
          <div className="w-[140px] h-[140px] p-1.5 border-r-0.5 border">
            <QRCode
              value={`otpauth://totp/SignalPlus Portal(${email})?secret=${mfaSecret}`}
              className='w-[126px] h-[126px]'
            />
          </div>
          <Input
            className='w-80!'
            value={mfaSecret ?? ''}
            readOnly
            suffix={
              <CopyOutlined
                onClick={() => {
                  navigator.clipboard.writeText(mfaSecret ?? '').then(
                    () => {
                      message.success('Copied successfully');
                    },
                    () => message.error('Copy failed'),
                  );
                }}
              />
            }
          />
        </div>
        <p />
        <Form.Item
          label={'Google 2FA Code'}
          shouldUpdate
          name="mfaCode"
          rules={[
            {
              required: true,
              message: 'Please input the 2FA code',
            },
            {
              validator: (_rule, value, callback) => {
                if (!value || value.length < 6) {
                  return Promise.reject(
                    'Please enter the Google Authenticator code',
                  );
                }
                return callback();
              },
            },
          ]}
        >
          <Input
            placeholder="Please enter the Google Authenticator code"
            size="large"
          />
        </Form.Item>
      </Form>
    </ModalContainer>
  );
};

export const DisableTFA = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
}) => {
  const [form] = Form.useForm();

  const [err, setErr] = useState<{ pwd?: string; mfa?: string }>({});
  const { runAsync: handleUnbindMfa, loading: unbindLoading } = useRequest(
    ({ mfaCode, password }: { password: string; mfaCode: string }) => {
      return unbindMfa({ password, mfaCode });
    },
    {
      manual: true,
      onSuccess: () => {
        setOpen(false);
        logout();
      },
      onError: err => message.error(getErrorMsg(err)),
    },
  );
  return (
    <ModalContainer
      open={open}
      title={'Disable Google 2FA'}
      onCancel={() => setOpen(false)}
      onOk={async () => {
        const { password, mfaCode } = form.getFieldsValue() ?? '';
        setErr({});
        if ((mfaCode ?? '')?.length < 6) {
          setErr(pre => ({
            ...pre,
            mfa: 'Please enter the Google Authenticator code',
          }));
          return Promise.reject();
        }
        if (!password?.length) {
          setErr(pre => ({ ...pre, pwd: 'Please enter the Password' }));
          return Promise.reject();
        }
        return handleUnbindMfa({ password, mfaCode });
      }}
      okText="Confirm"
      confirmLoading={unbindLoading}
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Password"
          shouldUpdate
          name="password"
          rules={[{ required: true }]}
        >
          <Input.Password
            placeholder="Please input the password"
            iconRender={visible =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>
        <Form.Item
          label={'Google 2FA Code'}
          shouldUpdate
          tooltip={{ title: 'If the 2FA is lost or unable to access, please contact with us', icon: <InfoCircleOutlined /> }}
          name="mfaCode"
          rules={[{ required: true }]}
        >
          <Input
            placeholder={'Please enter the Google Authenticator code'}
          />
        </Form.Item>
      </Form>
    </ModalContainer>
  );
};
